
import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

import schoolLogo from "./exellentkidslogo.jpg";
import schoolQRCode from "./excellentkidsqrcode.png";
import api from "../../../api/api"; 

/*
|--------------------------------------------------------------------------
| EXCELLENT KIDS FOUNDATION — GALVANIZED NAVY & KHAKI
|--------------------------------------------------------------------------
*/

const COLORS = {
  navy: "#1B2A41",
  deepNavy: "#131F31",
  royalBlue: "#34465F",
  khaki: "#C3B091",
  lightKhaki: "#E4D8C5",
  paleKhaki: "#F5F0E8",
  white: "#FFFFFF",
  offWhite: "#FAF8F4",
  lightBlue: "#EDF1F5",
  border: "#D8D0C4",
  text: "#263244",
  muted: "#687486",
};

/*
|--------------------------------------------------------------------------
| PRODUCTION-SAFE IMAGE URL HANDLER
|--------------------------------------------------------------------------
*/

const getStudentImageUrl = (student) => {
  if (!student) return null;

  const imageCandidates = [
    student.student_admission_image,
    student.student_image,
    student.profile_image,
    student.photo,
    student.image,
  ];

  const image = imageCandidates.find((candidate) => {
    if (typeof candidate === "string") {
      return candidate.trim().length > 0;
    }

    return (
      candidate &&
      typeof candidate === "object" &&
      Boolean(candidate.url || candidate.image || candidate.src)
    );
  });

  if (!image) return null;

  const rawUrl =
    typeof image === "string"
      ? image.trim()
      : image.url || image.image || image.src;

  if (!rawUrl || typeof rawUrl !== "string") {
    return null;
  }

  try {
    if (/^https?:\/\//i.test(rawUrl)) {
      return rawUrl;
    }

    const configuredBaseURL = api.defaults?.baseURL;

    if (!configuredBaseURL) {
      console.warn(
        "API baseURL is missing; cannot safely resolve student image:",
        rawUrl
      );

      return null;
    }

    const baseURL = new URL(
      configuredBaseURL,
      window.location.origin
    );

    const backendRoot = baseURL.pathname.replace(
      /\/api(?:\/v\d+)?\/?$/i,
      "/"
    );

    const backendOrigin = `${baseURL.origin}${backendRoot}`;

    if (rawUrl.startsWith("/")) {
      return new URL(rawUrl, baseURL.origin).href;
    }

    return new URL(rawUrl, backendOrigin).href;
  } catch (error) {
    console.error("Failed to resolve student photo URL:", {
      studentId: student.id,
      studentName: student.student_name || student.full_name,
      image: rawUrl,
      error,
    });

    return null;
  }
};

/*
|--------------------------------------------------------------------------
| SHARED SCHOOL LEVEL LABEL
|--------------------------------------------------------------------------
*/

const SchoolLevels = () => (
  <Text style={styles.schoolLevels}>
    DAYCARE  •  ELEMENTARY  •  JUNIOR HIGH
  </Text>
);

/*
|--------------------------------------------------------------------------
| PDF STYLES
|--------------------------------------------------------------------------
*/

const styles = StyleSheet.create({
  page: {
    padding: 18,
    backgroundColor: COLORS.white,
  },

  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignContent: "space-between",
    height: "100%",
  },

  cardWrapper: {
    width: "31.7%",
    height: "31.2%",
    marginBottom: 8,
  },

  card: {
    width: "100%",
    height: "100%",
    border: `1pt solid ${COLORS.navy}`,
    borderRadius: 9,
    overflow: "hidden",
    backgroundColor: COLORS.white,
    position: "relative",
  },

  backCard: {
    width: "100%",
    height: "100%",
    border: `1pt solid ${COLORS.navy}`,
    borderRadius: 9,
    overflow: "hidden",
    backgroundColor: COLORS.white,
    position: "relative",
  },

  topAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: COLORS.khaki,
  },

  bottomAccent: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: COLORS.navy,
  },

  watermarkGold: {
    position: "absolute",
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: COLORS.khaki,
    opacity: 0.2,
    top: 53,
    right: -30,
  },

  watermarkNavy: {
    position: "absolute",
    width: 88,
    height: 88,
    borderRadius: 44,
    backgroundColor: COLORS.royalBlue,
    opacity: 0.1,
    top: 83,
    right: 25,
  },

  watermarkSmall: {
    position: "absolute",
    width: 43,
    height: 43,
    borderRadius: 22,
    backgroundColor: COLORS.khaki,
    opacity: 0.15,
    bottom: 22,
    left: 42,
  },

  /*
  |--------------------------------------------------------------------------
  | FRONT HEADER
  |--------------------------------------------------------------------------
  */

  header: {
    height: 57,
    backgroundColor: COLORS.navy,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 7,
    paddingVertical: 4,
    position: "relative",
  },

  logoContainer: {
    width: 35,
    height: 35,
    borderRadius: 8,
    backgroundColor: COLORS.white,
    border: `1.5pt solid ${COLORS.khaki}`,
    alignItems: "center",
    justifyContent: "center",
    padding: 3,
    marginRight: 7,
  },

  schoolLogo: {
    width: 27,
    height: 27,
    objectFit: "contain",
    borderRadius: 5,
  },

  headerText: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingRight: 2,
  },

  schoolName: {
    fontSize: 7.4,
    fontWeight: "bold",
    color: COLORS.white,
    textAlign: "center",
    letterSpacing: 0.3,
  },

  schoolLevels: {
    fontSize: 4.6,
    fontWeight: "bold",
    color: COLORS.khaki,
    textAlign: "center",
    marginTop: 2,
    letterSpacing: 0.35,
  },

  schoolAddress: {
    fontSize: 3.8,
    color: COLORS.lightKhaki,
    textAlign: "center",
    marginTop: 2,
    lineHeight: 1.15,
  },

  schoolSubtitle: {
    fontSize: 4.8,
    fontWeight: "bold",
    color: COLORS.white,
    textAlign: "center",
    marginTop: 2,
    letterSpacing: 0.35,
  },

  headerGoldLine: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: COLORS.khaki,
  },

  /*
  |--------------------------------------------------------------------------
  | FRONT BODY
  |--------------------------------------------------------------------------
  */

  cardBody: {
    flexDirection: "row",
    flex: 1,
    paddingHorizontal: 7,
    paddingVertical: 6,
    alignItems: "center",
  },

  imageSection: {
    width: 67,
    alignItems: "center",
    justifyContent: "center",
  },

  imageContainer: {
    width: 61,
    height: 75,
    borderRadius: 7,
    border: `1.5pt solid ${COLORS.khaki}`,
    backgroundColor: COLORS.lightBlue,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },

  studentImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  noImage: {
    fontSize: 5.2,
    fontWeight: "bold",
    color: COLORS.muted,
    textAlign: "center",
  },

  photoLabel: {
    marginTop: 3,
    fontSize: 4.4,
    fontWeight: "bold",
    color: COLORS.navy,
    letterSpacing: 0.5,
  },

  details: {
    flex: 1,
    marginLeft: 7,
    justifyContent: "center",
    minWidth: 0,
  },

  studentIdBox: {
    backgroundColor: COLORS.navy,
    borderRadius: 5,
    borderLeft: `3pt solid ${COLORS.khaki}`,
    paddingHorizontal: 5,
    paddingVertical: 4,
    marginBottom: 5,
  },

  studentIdLabel: {
    fontSize: 4,
    fontWeight: "bold",
    color: COLORS.lightKhaki,
    marginBottom: 2,
    letterSpacing: 0.35,
  },

  studentId: {
    fontSize: 7.5,
    fontWeight: "bold",
    color: COLORS.white,
  },

  detailRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 3.5,
  },

  label: {
    width: 43,
    fontSize: 4.9,
    fontWeight: "bold",
    color: COLORS.royalBlue,
  },

  value: {
    flex: 1,
    fontSize: 5.8,
    fontWeight: "bold",
    color: COLORS.text,
    lineHeight: 1.15,
  },

  /*
  |--------------------------------------------------------------------------
  | SCHOOL QR CODE — FAR RIGHT OF STUDENT DETAILS
  |--------------------------------------------------------------------------
  */

  qrCodeSection: {
    width: 43,
    marginLeft: 4,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  schoolQRCode: {
    width: 37,
    height: 37,
    objectFit: "contain",
  },

  qrCodeLabel: {
    fontSize: 3.8,
    fontWeight: "bold",
    color: COLORS.navy,
    textAlign: "center",
    marginTop: 3,
    letterSpacing: 0.2,
  },

  /*
  |--------------------------------------------------------------------------
  | FRONT FOOTER
  |--------------------------------------------------------------------------
  */

  footer: {
    minHeight: 18,
    backgroundColor: COLORS.offWhite,
    borderTop: `0.7pt solid ${COLORS.border}`,
    paddingHorizontal: 6,
    paddingVertical: 4,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },

  footerText: {
    maxWidth: "63%",
    fontSize: 4,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  footerYear: {
    maxWidth: "35%",
    fontSize: 4,
    fontWeight: "bold",
    color: COLORS.royalBlue,
    textAlign: "right",
  },

  /*
  |--------------------------------------------------------------------------
  | BACK HEADER
  |--------------------------------------------------------------------------
  */

  backTop: {
    height: 55,
    backgroundColor: COLORS.navy,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 7,
    paddingVertical: 4,
    position: "relative",
  },

  backLogoContainer: {
    width: 34,
    height: 34,
    borderRadius: 8,
    backgroundColor: COLORS.white,
    border: `1.5pt solid ${COLORS.khaki}`,
    alignItems: "center",
    justifyContent: "center",
    padding: 3,
    marginRight: 7,
  },

  backLogo: {
    width: 26,
    height: 26,
    objectFit: "contain",
    borderRadius: 5,
  },

  backHeaderText: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },

  backTitle: {
    fontSize: 7.1,
    fontWeight: "bold",
    color: COLORS.white,
    textAlign: "center",
    letterSpacing: 0.3,
  },

  backSchoolLevels: {
    fontSize: 4.5,
    fontWeight: "bold",
    color: COLORS.khaki,
    marginTop: 2,
    textAlign: "center",
    letterSpacing: 0.3,
  },

  backAddress: {
    fontSize: 4.2,
    color: COLORS.lightKhaki,
    marginTop: 2,
    textAlign: "center",
  },

  backSubtitle: {
    fontSize: 4.6,
    fontWeight: "bold",
    color: COLORS.white,
    marginTop: 2,
    textAlign: "center",
  },

  backGoldLine: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: COLORS.khaki,
  },

  /*
  |--------------------------------------------------------------------------
  | BACK CONTENT
  |--------------------------------------------------------------------------
  */

  backContent: {
    flex: 1,
    paddingHorizontal: 7,
    paddingVertical: 6,
    justifyContent: "space-between",
  },

  instructionBox: {
    backgroundColor: COLORS.paleKhaki,
    border: `0.7pt solid ${COLORS.khaki}`,
    borderRadius: 6,
    padding: 5,
  },

  instructionHeading: {
    fontSize: 5.5,
    fontWeight: "bold",
    color: COLORS.navy,
    textAlign: "center",
    marginBottom: 3,
    letterSpacing: 0.4,
  },

  instructions: {
    fontSize: 4.9,
    lineHeight: 1.35,
    color: COLORS.text,
    textAlign: "center",
  },

  rulesBox: {
    backgroundColor: COLORS.offWhite,
    border: `0.7pt solid ${COLORS.border}`,
    borderLeft: `2pt solid ${COLORS.navy}`,
    borderRadius: 5,
    padding: 5,
    marginTop: 4,
  },

  rulesHeading: {
    fontSize: 5.3,
    fontWeight: "bold",
    color: COLORS.navy,
    textAlign: "center",
    marginBottom: 3,
    letterSpacing: 0.3,
  },

  rule: {
    fontSize: 4.65,
    color: COLORS.text,
    marginBottom: 2.1,
    lineHeight: 1.2,
  },

  /*
  |--------------------------------------------------------------------------
  | EMERGENCY CONTACT
  |--------------------------------------------------------------------------
  */

  emergencyBox: {
    marginTop: 4,
    backgroundColor: COLORS.navy,
    borderRadius: 5,
    borderLeft: `3pt solid ${COLORS.khaki}`,
    paddingVertical: 4,
    paddingHorizontal: 5,
    alignItems: "center",
  },

  emergency: {
    fontSize: 5.2,
    fontWeight: "bold",
    color: COLORS.lightKhaki,
    textAlign: "center",
  },

  emergencySubtext: {
    fontSize: 4.5,
    color: COLORS.white,
    marginTop: 1.5,
    textAlign: "center",
  },

  /*
  |--------------------------------------------------------------------------
  | SIGNATURES
  |--------------------------------------------------------------------------
  */

  signatureSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 5,
  },

  signatureBlock: {
    width: "42%",
    alignItems: "center",
  },

  signature: {
    width: "100%",
    borderTop: `0.8pt solid ${COLORS.navy}`,
    paddingTop: 3,
    textAlign: "center",
    fontSize: 4.4,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  /*
  |--------------------------------------------------------------------------
  | BACK FOOTER
  |--------------------------------------------------------------------------
  */

  backFooter: {
    marginTop: 4,
    paddingTop: 4,
    borderTop: `0.7pt solid ${COLORS.khaki}`,
    alignItems: "center",
  },

  backFooterText: {
    fontSize: 4.15,
    color: COLORS.navy,
    fontWeight: "bold",
    textAlign: "center",
    letterSpacing: 0.2,
  },
});

/*
|--------------------------------------------------------------------------
| STUDENT FRONT ID CARD
|--------------------------------------------------------------------------
*/

const StudentFrontCard = ({ student, academicYear }) => {
  const year =
    student.academic_year_name ||
    academicYear?.name ||
    academicYear?.academic_year ||
    (typeof academicYear === "string" ? academicYear : null) ||
    "N/A";

  const studentPhoto = getStudentImageUrl(student);

  const studentName =
    student.student_name ||
    student.full_name ||
    student.full_name_name ||
    "N/A";

  const className =
    typeof student.grade_class === "object"
      ? student.grade_class?.name
      : student.grade_class ||
        student.grade_class_name ||
        "N/A";

  const division =
    typeof student.division === "object"
      ? student.division?.name
      : student.division ||
        student.division_assigned_name ||
        "N/A";

  return (
    <View style={styles.card}>
      <View style={styles.topAccent} />

      <View style={styles.watermarkGold} />
      <View style={styles.watermarkNavy} />
      <View style={styles.watermarkSmall} />

      {/* HEADER */}

      <View style={styles.header}>
        <View style={styles.logoContainer}>
          <Image src={schoolLogo} style={styles.schoolLogo} />
        </View>

        <View style={styles.headerText}>
          <Text style={styles.schoolName}>
            EXCELLENT KIDS FOUNDATION
          </Text>

          <SchoolLevels />

          <Text style={styles.schoolAddress}>
            Greenland Community, Upper Johnsonville Township,
            Left Bank 2B, Montserrado County, Liberia
          </Text>

          <Text style={styles.schoolSubtitle}>
            STUDENT IDENTIFICATION CARD
          </Text>
        </View>

        <View style={styles.headerGoldLine} />
      </View>

      {/* STUDENT INFORMATION */}

      <View style={styles.cardBody}>
        {/* STUDENT PHOTO */}

        <View style={styles.imageSection}>
          <View style={styles.imageContainer}>
            {studentPhoto ? (
              <Image
                src={studentPhoto}
                style={styles.studentImage}
              />
            ) : (
              <Text style={styles.noImage}>NO PHOTO</Text>
            )}
          </View>

          <Text style={styles.photoLabel}>STUDENT</Text>
        </View>

        {/* STUDENT DETAILS */}

        <View style={styles.details}>
          <View style={styles.studentIdBox}>
            <Text style={styles.studentIdLabel}>
              OFFICIAL STUDENT ID
            </Text>

            <Text style={styles.studentId}>
              {student.card_number ||
                student.admission_id ||
                "STUDENT ID"}
            </Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.label}>NAME</Text>
            <Text style={styles.value}>{studentName}</Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.label}>CLASS</Text>
            <Text style={styles.value}>{className}</Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.label}>DIVISION</Text>
            <Text style={styles.value}>{division}</Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.label}>GENDER</Text>
            <Text style={styles.value}>
              {student.gender || "N/A"}
            </Text>
          </View>

          <View style={styles.detailRow}>
            <Text style={styles.label}>SCHOOL YEAR</Text>
            <Text style={styles.value}>{year}</Text>
          </View>
        </View>

        {/* SCHOOL QR CODE — FAR RIGHT */}

        <View style={styles.qrCodeSection}>
          <Image
            src={schoolQRCode}
            style={styles.schoolQRCode}
          />

          <Text style={styles.qrCodeLabel}>
            SCHOOL QR
          </Text>
        </View>
      </View>

      {/* FOOTER */}

      <View style={styles.footer}>
        <Text style={styles.footerText}>
          PROPERTY OF EXCELLENT KIDS FOUNDATION
        </Text>

        <Text style={styles.footerYear}>
          VALID: {year}
        </Text>
      </View>

      <View style={styles.bottomAccent} />
    </View>
  );
};

/*
|--------------------------------------------------------------------------
| CREATE NINE PRINTING SLOTS
|--------------------------------------------------------------------------
*/

const createNineSlots = (students) => {
  const slots = [...students];

  while (slots.length < 9) {
    slots.push(null);
  }

  return slots.slice(0, 9);
};

/*
|--------------------------------------------------------------------------
| STUDENT BACK ID CARD
|--------------------------------------------------------------------------
*/

const StudentBackCard = () => (
  <View style={styles.backCard}>
    <View style={styles.topAccent} />

    <View style={styles.backTop}>
      <View style={styles.backLogoContainer}>
        <Image src={schoolLogo} style={styles.backLogo} />
      </View>

      <View style={styles.backHeaderText}>
        <Text style={styles.backTitle}>
          EXCELLENT KIDS FOUNDATION
        </Text>

        <Text style={styles.backSchoolLevels}>
          DAYCARE  •  ELEMENTARY  •  JUNIOR HIGH SCHOOL
        </Text>

        <Text style={styles.backAddress}>
          Greenland Community, Upper Johnsonville Township, Liberia
        </Text>

        <Text style={styles.backSubtitle}>
          STUDENT IDENTIFICATION CARD
        </Text>
      </View>

      <View style={styles.backGoldLine} />
    </View>

    <View style={styles.backContent}>
      <View style={styles.instructionBox}>
        <Text style={styles.instructionHeading}>
          IMPORTANT NOTICE
        </Text>

        <Text style={styles.instructions}>
          This identification card remains the property of
          Excellent Kids Foundation. If found, please return
          it to the school administration or the nearest
          police station.
        </Text>
      </View>

      <View style={styles.rulesBox}>
        <Text style={styles.rulesHeading}>
          CARD REGULATIONS
        </Text>

        <Text style={styles.rule}>
          • Present this card when requested by authorized
          school officials.
        </Text>

        <Text style={styles.rule}>
          • This card is valid only for the academic year
          printed on the front.
        </Text>

        <Text style={styles.rule}>
          • Do not lend or transfer this card to another person.
        </Text>

        <Text style={styles.rule}>
          • Report lost, stolen, or damaged cards to the
          school administration immediately.
        </Text>
      </View>

      <View style={styles.emergencyBox}>
        <Text style={styles.emergency}>
          EMERGENCY CONTACT
        </Text>

        <Text style={styles.emergencySubtext}>
          PLEASE CONTACT THE SCHOOL ADMINISTRATION
        </Text>
      </View>

      <View style={styles.signatureSection}>
        <View style={styles.signatureBlock}>
          <Text style={styles.signature}>
            AUTHORIZED SIGNATURE
          </Text>
        </View>

        <View style={styles.signatureBlock}>
          <Text style={styles.signature}>
            OFFICIAL SCHOOL STAMP
          </Text>
        </View>
      </View>

      <View style={styles.backFooter}>
        <Text style={styles.backFooterText}>
          CHARACTER • EDUCATION • EXCELLENCE • SERVICE
        </Text>
      </View>
    </View>

    <View style={styles.bottomAccent} />
  </View>
);

/*
|--------------------------------------------------------------------------
| MAIN PDF COMPONENT
|--------------------------------------------------------------------------
*/

const StudentIDCardPDF = ({
  students = [],
  academicYear = null,
}) => {
  const pages = [];

  for (let index = 0; index < students.length; index += 9) {
    pages.push(students.slice(index, index + 9));
  }

  return (
    <Document
      title="Excellent Kids Foundation — Student ID Cards"
      author="Excellent Kids Foundation"
      subject="Official Student Identification Cards"
    >
      {pages.map((pageStudents, pageIndex) => {
        const slots = createNineSlots(pageStudents);

        return (
          <React.Fragment key={`card-sheet-${pageIndex}`}>
            {/* FRONT SHEET */}

            <Page
              size="A4"
              orientation="landscape"
              style={styles.page}
            >
              <View style={styles.grid}>
                {slots.map((student, index) => (
                  <View
                    key={`front-${pageIndex}-${index}`}
                    style={styles.cardWrapper}
                  >
                    {student && (
                      <StudentFrontCard
                        student={student}
                        academicYear={academicYear}
                      />
                    )}
                  </View>
                ))}
              </View>
            </Page>

            {/* BACK SHEET */}

            <Page
              size="A4"
              orientation="landscape"
              style={styles.page}
            >
              <View style={styles.grid}>
                {slots.map((student, index) => (
                  <View
                    key={`back-${pageIndex}-${index}`}
                    style={styles.cardWrapper}
                  >
                    {student && <StudentBackCard />}
                  </View>
                ))}
              </View>
            </Page>
          </React.Fragment>
        );
      })}
    </Document>
  );
};

export default StudentIDCardPDF;




// import React from "react";
// import {
//   Document,
//   Page,
//   Text,
//   View,
//   Image,
//   StyleSheet,
// } from "@react-pdf/renderer";

// import schoolLogo from "./exellentkidslogo.jpg";
// import api from "../../../api/api";

// /*
// |--------------------------------------------------------------------------
// | EXCELLENT KIDS FOUNDATION — GALVANIZED NAVY & KHAKI
// |--------------------------------------------------------------------------
// */

// const COLORS = {
//   navy: "#1B2A41",
//   deepNavy: "#131F31",
//   royalBlue: "#34465F",
//   khaki: "#C3B091",
//   lightKhaki: "#E4D8C5",
//   paleKhaki: "#F5F0E8",
//   white: "#FFFFFF",
//   offWhite: "#FAF8F4",
//   lightBlue: "#EDF1F5",
//   border: "#D8D0C4",
//   text: "#263244",
//   muted: "#687486",
// };

// /*
// |--------------------------------------------------------------------------
// | PRODUCTION-SAFE IMAGE URL HANDLER
// |--------------------------------------------------------------------------
// |
// | Preferred source:
// | 1. student_admission_image
// | 2. student_image
// | 3. profile_image
// | 4. Other compatible image fields
// |
// | Relative URLs are resolved against the configured API server.
// | Absolute HTTP/HTTPS URLs are preserved.
// |--------------------------------------------------------------------------
// */

// const getStudentImageUrl = (student) => {
//   if (!student) return null;

//   const imageCandidates = [
//     student.student_admission_image,
//     student.student_image,
//     student.profile_image,
//     student.photo,
//     student.image,
//   ];

//   const image = imageCandidates.find((candidate) => {
//     if (typeof candidate === "string") {
//       return candidate.trim().length > 0;
//     }

//     return (
//       candidate &&
//       typeof candidate === "object" &&
//       Boolean(candidate.url || candidate.image || candidate.src)
//     );
//   });

//   if (!image) return null;

//   const rawUrl =
//     typeof image === "string"
//       ? image.trim()
//       : image.url || image.image || image.src;

//   if (!rawUrl || typeof rawUrl !== "string") {
//     return null;
//   }

//   try {
//     // Preserve fully qualified image URLs.
//     if (/^https?:\/\//i.test(rawUrl)) {
//       return rawUrl;
//     }

//     /*
//      * IMPORTANT:
//      * api.defaults.baseURL should point to your backend API.
//      *
//      * Example:
//      * https://excellentkidsfoundation.online/api/
//      *
//      * /media/example.jpg resolves to:
//      * https://excellentkidsfoundation.online/media/example.jpg
//      */

//     const configuredBaseURL = api.defaults?.baseURL;

//     if (!configuredBaseURL) {
//       console.warn(
//         "API baseURL is missing; cannot safely resolve student image:",
//         rawUrl
//       );

//       return null;
//     }

//     const baseURL = new URL(
//       configuredBaseURL,
//       window.location.origin
//     );

//     // Remove a trailing /api/ or /api/v1/ to get the backend root.
//     const backendRoot = baseURL.pathname.replace(
//       /\/api(?:\/v\d+)?\/?$/i,
//       "/"
//     );

//     const backendOrigin = `${baseURL.origin}${backendRoot}`;

//     /*
//      * A leading slash indicates a server-root path.
//      * A path without a leading slash is resolved against the
//      * backend root.
//      */
//     if (rawUrl.startsWith("/")) {
//       return new URL(rawUrl, baseURL.origin).href;
//     }

//     return new URL(rawUrl, backendOrigin).href;
//   } catch (error) {
//     console.error("Failed to resolve student photo URL:", {
//       studentId: student.id,
//       studentName: student.student_name || student.full_name,
//       image: rawUrl,
//       error,
//     });

//     return null;
//   }
// };

// /*
// |--------------------------------------------------------------------------
// | SHARED SCHOOL LEVEL LABEL
// |--------------------------------------------------------------------------
// */

// const SchoolLevels = () => (
//   <Text style={styles.schoolLevels}>
//     DAYCARE  •  ELEMENTARY  •  JUNIOR HIGH
//   </Text>
// );

// /*
// |--------------------------------------------------------------------------
// | PDF STYLES
// |--------------------------------------------------------------------------
// */

// const styles = StyleSheet.create({
//   page: {
//     padding: 18,
//     backgroundColor: COLORS.white,
//   },

//   grid: {
//     flexDirection: "row",
//     flexWrap: "wrap",
//     justifyContent: "space-between",
//     alignContent: "space-between",
//     height: "100%",
//   },

//   cardWrapper: {
//     width: "31.7%",
//     height: "31.2%",
//     marginBottom: 8,
//   },

//   card: {
//     width: "100%",
//     height: "100%",
//     border: `1pt solid ${COLORS.navy}`,
//     borderRadius: 9,
//     overflow: "hidden",
//     backgroundColor: COLORS.white,
//     position: "relative",
//   },

//   backCard: {
//     width: "100%",
//     height: "100%",
//     border: `1pt solid ${COLORS.navy}`,
//     borderRadius: 9,
//     overflow: "hidden",
//     backgroundColor: COLORS.white,
//     position: "relative",
//   },

//   topAccent: {
//     position: "absolute",
//     top: 0,
//     left: 0,
//     right: 0,
//     height: 3,
//     backgroundColor: COLORS.khaki,
//   },

//   bottomAccent: {
//     position: "absolute",
//     bottom: 0,
//     left: 0,
//     right: 0,
//     height: 3,
//     backgroundColor: COLORS.navy,
//   },

//   watermarkGold: {
//     position: "absolute",
//     width: 100,
//     height: 100,
//     borderRadius: 50,
//     backgroundColor: COLORS.khaki,
//     opacity: 0.2,
//     top: 53,
//     right: -30,
//   },

//   watermarkNavy: {
//     position: "absolute",
//     width: 88,
//     height: 88,
//     borderRadius: 44,
//     backgroundColor: COLORS.royalBlue,
//     opacity: 0.1,
//     top: 83,
//     right: 25,
//   },

//   watermarkSmall: {
//     position: "absolute",
//     width: 43,
//     height: 43,
//     borderRadius: 22,
//     backgroundColor: COLORS.khaki,
//     opacity: 0.15,
//     bottom: 22,
//     left: 42,
//   },

//   /*
//   |--------------------------------------------------------------------------
//   | FRONT HEADER
//   |--------------------------------------------------------------------------
//   */

//   header: {
//     height: 57,
//     backgroundColor: COLORS.navy,
//     flexDirection: "row",
//     alignItems: "center",
//     paddingHorizontal: 7,
//     paddingVertical: 4,
//     position: "relative",
//   },

//   logoContainer: {
//     width: 35,
//     height: 35,
//     borderRadius: 8,
//     backgroundColor: COLORS.white,
//     border: `1.5pt solid ${COLORS.khaki}`,
//     alignItems: "center",
//     justifyContent: "center",
//     padding: 3,
//     marginRight: 7,
//   },

//   schoolLogo: {
//     width: 27,
//     height: 27,
//     objectFit: "contain",
//     borderRadius: 5,
//   },

//   headerText: {
//     flex: 1,
//     justifyContent: "center",
//     alignItems: "center",
//     paddingRight: 2,
//   },

//   schoolName: {
//     fontSize: 7.4,
//     fontWeight: "bold",
//     color: COLORS.white,
//     textAlign: "center",
//     letterSpacing: 0.3,
//   },

//   schoolLevels: {
//     fontSize: 4.6,
//     fontWeight: "bold",
//     color: COLORS.khaki,
//     textAlign: "center",
//     marginTop: 2,
//     letterSpacing: 0.35,
//   },

//   schoolAddress: {
//     fontSize: 3.8,
//     color: COLORS.lightKhaki,
//     textAlign: "center",
//     marginTop: 2,
//     lineHeight: 1.15,
//   },

//   schoolSubtitle: {
//     fontSize: 4.8,
//     fontWeight: "bold",
//     color: COLORS.white,
//     textAlign: "center",
//     marginTop: 2,
//     letterSpacing: 0.35,
//   },

//   headerGoldLine: {
//     position: "absolute",
//     bottom: 0,
//     left: 0,
//     right: 0,
//     height: 2,
//     backgroundColor: COLORS.khaki,
//   },

//   /*
//   |--------------------------------------------------------------------------
//   | FRONT BODY
//   |--------------------------------------------------------------------------
//   */

//   cardBody: {
//     flexDirection: "row",
//     flex: 1,
//     paddingHorizontal: 7,
//     paddingVertical: 6,
//   },

//   imageSection: {
//     width: 67,
//     alignItems: "center",
//     justifyContent: "center",
//   },

//   imageContainer: {
//     width: 61,
//     height: 75,
//     borderRadius: 7,
//     border: `1.5pt solid ${COLORS.khaki}`,
//     backgroundColor: COLORS.lightBlue,
//     overflow: "hidden",
//     alignItems: "center",
//     justifyContent: "center",
//   },

//   studentImage: {
//     width: "100%",
//     height: "100%",
//     objectFit: "cover",
//   },

//   noImage: {
//     fontSize: 5.2,
//     fontWeight: "bold",
//     color: COLORS.muted,
//     textAlign: "center",
//   },

//   photoLabel: {
//     marginTop: 3,
//     fontSize: 4.4,
//     fontWeight: "bold",
//     color: COLORS.navy,
//     letterSpacing: 0.5,
//   },

//   details: {
//     flex: 1,
//     marginLeft: 7,
//     justifyContent: "center",
//   },

//   studentIdBox: {
//     backgroundColor: COLORS.navy,
//     borderRadius: 5,
//     borderLeft: `3pt solid ${COLORS.khaki}`,
//     paddingHorizontal: 5,
//     paddingVertical: 4,
//     marginBottom: 5,
//   },

//   studentIdLabel: {
//     fontSize: 4,
//     fontWeight: "bold",
//     color: COLORS.lightKhaki,
//     marginBottom: 2,
//     letterSpacing: 0.35,
//   },

//   studentId: {
//     fontSize: 7.5,
//     fontWeight: "bold",
//     color: COLORS.white,
//   },

//   detailRow: {
//     flexDirection: "row",
//     alignItems: "flex-start",
//     marginBottom: 3.5,
//   },

//   label: {
//     width: 43,
//     fontSize: 4.9,
//     fontWeight: "bold",
//     color: COLORS.royalBlue,
//   },

//   value: {
//     flex: 1,
//     fontSize: 5.8,
//     fontWeight: "bold",
//     color: COLORS.text,
//     lineHeight: 1.15,
//   },

//   /*
//   |--------------------------------------------------------------------------
//   | FRONT FOOTER
//   |--------------------------------------------------------------------------
//   */

//   footer: {
//     minHeight: 18,
//     backgroundColor: COLORS.offWhite,
//     borderTop: `0.7pt solid ${COLORS.border}`,
//     paddingHorizontal: 6,
//     paddingVertical: 4,
//     flexDirection: "row",
//     alignItems: "center",
//     justifyContent: "space-between",
//   },

//   footerText: {
//     maxWidth: "63%",
//     fontSize: 4,
//     fontWeight: "bold",
//     color: COLORS.navy,
//   },

//   footerYear: {
//     maxWidth: "35%",
//     fontSize: 4,
//     fontWeight: "bold",
//     color: COLORS.royalBlue,
//     textAlign: "right",
//   },

//   /*
//   |--------------------------------------------------------------------------
//   | BACK HEADER
//   |--------------------------------------------------------------------------
//   */

//   backTop: {
//     height: 55,
//     backgroundColor: COLORS.navy,
//     flexDirection: "row",
//     alignItems: "center",
//     paddingHorizontal: 7,
//     paddingVertical: 4,
//     position: "relative",
//   },

//   backLogoContainer: {
//     width: 34,
//     height: 34,
//     borderRadius: 8,
//     backgroundColor: COLORS.white,
//     border: `1.5pt solid ${COLORS.khaki}`,
//     alignItems: "center",
//     justifyContent: "center",
//     padding: 3,
//     marginRight: 7,
//   },

//   backLogo: {
//     width: 26,
//     height: 26,
//     objectFit: "contain",
//     borderRadius: 5,
//   },

//   backHeaderText: {
//     flex: 1,
//     alignItems: "center",
//     justifyContent: "center",
//   },

//   backTitle: {
//     fontSize: 7.1,
//     fontWeight: "bold",
//     color: COLORS.white,
//     textAlign: "center",
//     letterSpacing: 0.3,
//   },

//   backSchoolLevels: {
//     fontSize: 4.5,
//     fontWeight: "bold",
//     color: COLORS.khaki,
//     marginTop: 2,
//     textAlign: "center",
//     letterSpacing: 0.3,
//   },

//   backAddress: {
//     fontSize: 4.2,
//     color: COLORS.lightKhaki,
//     marginTop: 2,
//     textAlign: "center",
//   },

//   backSubtitle: {
//     fontSize: 4.6,
//     fontWeight: "bold",
//     color: COLORS.white,
//     marginTop: 2,
//     textAlign: "center",
//   },

//   backGoldLine: {
//     position: "absolute",
//     bottom: 0,
//     left: 0,
//     right: 0,
//     height: 2,
//     backgroundColor: COLORS.khaki,
//   },

//   /*
//   |--------------------------------------------------------------------------
//   | BACK CONTENT
//   |--------------------------------------------------------------------------
//   */

//   backContent: {
//     flex: 1,
//     paddingHorizontal: 7,
//     paddingVertical: 6,
//     justifyContent: "space-between",
//   },

//   instructionBox: {
//     backgroundColor: COLORS.paleKhaki,
//     border: `0.7pt solid ${COLORS.khaki}`,
//     borderRadius: 6,
//     padding: 5,
//   },

//   instructionHeading: {
//     fontSize: 5.5,
//     fontWeight: "bold",
//     color: COLORS.navy,
//     textAlign: "center",
//     marginBottom: 3,
//     letterSpacing: 0.4,
//   },

//   instructions: {
//     fontSize: 4.9,
//     lineHeight: 1.35,
//     color: COLORS.text,
//     textAlign: "center",
//   },

//   rulesBox: {
//     backgroundColor: COLORS.offWhite,
//     border: `0.7pt solid ${COLORS.border}`,
//     borderLeft: `2pt solid ${COLORS.navy}`,
//     borderRadius: 5,
//     padding: 5,
//     marginTop: 4,
//   },

//   rulesHeading: {
//     fontSize: 5.3,
//     fontWeight: "bold",
//     color: COLORS.navy,
//     textAlign: "center",
//     marginBottom: 3,
//     letterSpacing: 0.3,
//   },

//   rule: {
//     fontSize: 4.65,
//     color: COLORS.text,
//     marginBottom: 2.1,
//     lineHeight: 1.2,
//   },

//   /*
//   |--------------------------------------------------------------------------
//   | EMERGENCY CONTACT
//   |--------------------------------------------------------------------------
//   */

//   emergencyBox: {
//     marginTop: 4,
//     backgroundColor: COLORS.navy,
//     borderRadius: 5,
//     borderLeft: `3pt solid ${COLORS.khaki}`,
//     paddingVertical: 4,
//     paddingHorizontal: 5,
//     alignItems: "center",
//   },

//   emergency: {
//     fontSize: 5.2,
//     fontWeight: "bold",
//     color: COLORS.lightKhaki,
//     textAlign: "center",
//   },

//   emergencySubtext: {
//     fontSize: 4.5,
//     color: COLORS.white,
//     marginTop: 1.5,
//     textAlign: "center",
//   },

//   /*
//   |--------------------------------------------------------------------------
//   | SIGNATURES
//   |--------------------------------------------------------------------------
//   */

//   signatureSection: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "flex-end",
//     marginTop: 5,
//   },

//   signatureBlock: {
//     width: "42%",
//     alignItems: "center",
//   },

//   signature: {
//     width: "100%",
//     borderTop: `0.8pt solid ${COLORS.navy}`,
//     paddingTop: 3,
//     textAlign: "center",
//     fontSize: 4.4,
//     fontWeight: "bold",
//     color: COLORS.navy,
//   },

//   /*
//   |--------------------------------------------------------------------------
//   | BACK FOOTER
//   |--------------------------------------------------------------------------
//   */

//   backFooter: {
//     marginTop: 4,
//     paddingTop: 4,
//     borderTop: `0.7pt solid ${COLORS.khaki}`,
//     alignItems: "center",
//   },

//   backFooterText: {
//     fontSize: 4.15,
//     color: COLORS.navy,
//     fontWeight: "bold",
//     textAlign: "center",
//     letterSpacing: 0.2,
//   },
// });

// /*
// |--------------------------------------------------------------------------
// | STUDENT FRONT ID CARD
// |--------------------------------------------------------------------------
// */

// const StudentFrontCard = ({ student, academicYear }) => {
//   const year =
//     student.academic_year_name ||
//     academicYear?.name ||
//     academicYear?.academic_year ||
//     (typeof academicYear === "string" ? academicYear : null) ||
//     "N/A";

//   const studentPhoto = getStudentImageUrl(student);

//   const studentName =
//     student.student_name ||
//     student.full_name ||
//     student.full_name_name ||
//     "N/A";

//   const className =
//     typeof student.grade_class === "object"
//       ? student.grade_class?.name
//       : student.grade_class ||
//         student.grade_class_name ||
//         "N/A";

//   const division =
//     typeof student.division === "object"
//       ? student.division?.name
//       : student.division ||
//         student.division_assigned_name ||
//         "N/A";

//   return (
//     <View style={styles.card}>
//       <View style={styles.topAccent} />

//       <View style={styles.watermarkGold} />
//       <View style={styles.watermarkNavy} />
//       <View style={styles.watermarkSmall} />

//       {/* HEADER */}

//       <View style={styles.header}>
//         <View style={styles.logoContainer}>
//           <Image src={schoolLogo} style={styles.schoolLogo} />
//         </View>

//         <View style={styles.headerText}>
//           <Text style={styles.schoolName}>
//             EXCELLENT KIDS FOUNDATION
//           </Text>

//           <SchoolLevels />

//           <Text style={styles.schoolAddress}>
//             Greenland Community, Upper Johnsonville Township,
//             Left Bank 2B, Montserrado County, Liberia
//           </Text>

//           <Text style={styles.schoolSubtitle}>
//             STUDENT IDENTIFICATION CARD
//           </Text>
//         </View>

//         <View style={styles.headerGoldLine} />
//       </View>

//       {/* STUDENT INFORMATION */}

//       <View style={styles.cardBody}>
//         <View style={styles.imageSection}>
//           <View style={styles.imageContainer}>
//             {studentPhoto ? (
//               <Image
//                 src={studentPhoto}
//                 style={styles.studentImage}
//               />
//             ) : (
//               <Text style={styles.noImage}>NO PHOTO</Text>
//             )}
//           </View>

//           <Text style={styles.photoLabel}>STUDENT</Text>
//         </View>

//         <View style={styles.details}>
//           <View style={styles.studentIdBox}>
//             <Text style={styles.studentIdLabel}>
//               OFFICIAL STUDENT ID
//             </Text>

//             <Text style={styles.studentId}>
//               {student.card_number ||
//                 student.admission_id ||
//                 "STUDENT ID"}
//             </Text>
//           </View>

//           <View style={styles.detailRow}>
//             <Text style={styles.label}>NAME</Text>
//             <Text style={styles.value}>{studentName}</Text>
//           </View>

//           <View style={styles.detailRow}>
//             <Text style={styles.label}>CLASS</Text>
//             <Text style={styles.value}>{className}</Text>
//           </View>

//           <View style={styles.detailRow}>
//             <Text style={styles.label}>DIVISION</Text>
//             <Text style={styles.value}>{division}</Text>
//           </View>

//           <View style={styles.detailRow}>
//             <Text style={styles.label}>GENDER</Text>
//             <Text style={styles.value}>
//               {student.gender || "N/A"}
//             </Text>
//           </View>

//           <View style={styles.detailRow}>
//             <Text style={styles.label}>SCHOOL YEAR</Text>
//             <Text style={styles.value}>{year}</Text>
//           </View>
//         </View>
//       </View>

//       {/* FOOTER */}

//       <View style={styles.footer}>
//         <Text style={styles.footerText}>
//           PROPERTY OF EXCELLENT KIDS FOUNDATION
//         </Text>

//         <Text style={styles.footerYear}>
//           VALID: {year}
//         </Text>
//       </View>

//       <View style={styles.bottomAccent} />
//     </View>
//   );
// };

// /*
// |--------------------------------------------------------------------------
// | CREATE NINE PRINTING SLOTS
// |--------------------------------------------------------------------------
// */

// const createNineSlots = (students) => {
//   const slots = [...students];

//   while (slots.length < 9) {
//     slots.push(null);
//   }

//   return slots.slice(0, 9);
// };

// /*
// |--------------------------------------------------------------------------
// | STUDENT BACK ID CARD
// |--------------------------------------------------------------------------
// */

// const StudentBackCard = () => (
//   <View style={styles.backCard}>
//     <View style={styles.topAccent} />

//     <View style={styles.backTop}>
//       <View style={styles.backLogoContainer}>
//         <Image src={schoolLogo} style={styles.backLogo} />
//       </View>

//       <View style={styles.backHeaderText}>
//         <Text style={styles.backTitle}>
//           EXCELLENT KIDS FOUNDATION
//         </Text>

//         <Text style={styles.backSchoolLevels}>
//           DAYCARE  •  ELEMENTARY  •  JUNIOR HIGH SCHOOL
//         </Text>

//         <Text style={styles.backAddress}>
//           Greenland Community, Upper Johnsonville Township, Liberia
//         </Text>

//         <Text style={styles.backSubtitle}>
//           STUDENT IDENTIFICATION CARD
//         </Text>
//       </View>

//       <View style={styles.backGoldLine} />
//     </View>

//     <View style={styles.backContent}>
//       <View style={styles.instructionBox}>
//         <Text style={styles.instructionHeading}>
//           IMPORTANT NOTICE
//         </Text>

//         <Text style={styles.instructions}>
//           This identification card remains the property of
//           Excellent Kids Foundation. If found, please return
//           it to the school administration or the nearest
//           police station.
//         </Text>
//       </View>

//       <View style={styles.rulesBox}>
//         <Text style={styles.rulesHeading}>
//           CARD REGULATIONS
//         </Text>

//         <Text style={styles.rule}>
//           • Present this card when requested by authorized
//           school officials.
//         </Text>

//         <Text style={styles.rule}>
//           • This card is valid only for the academic year
//           printed on the front.
//         </Text>

//         <Text style={styles.rule}>
//           • Do not lend or transfer this card to another person.
//         </Text>

//         <Text style={styles.rule}>
//           • Report lost, stolen, or damaged cards to the
//           school administration immediately.
//         </Text>
//       </View>

//       <View style={styles.emergencyBox}>
//         <Text style={styles.emergency}>
//           EMERGENCY CONTACT
//         </Text>

//         <Text style={styles.emergencySubtext}>
//           PLEASE CONTACT THE SCHOOL ADMINISTRATION
//         </Text>
//       </View>

//       <View style={styles.signatureSection}>
//         <View style={styles.signatureBlock}>
//           <Text style={styles.signature}>
//             AUTHORIZED SIGNATURE
//           </Text>
//         </View>

//         <View style={styles.signatureBlock}>
//           <Text style={styles.signature}>
//             OFFICIAL SCHOOL STAMP
//           </Text>
//         </View>
//       </View>

//       <View style={styles.backFooter}>
//         <Text style={styles.backFooterText}>
//           CHARACTER • EDUCATION • EXCELLENCE • SERVICE
//         </Text>
//       </View>
//     </View>

//     <View style={styles.bottomAccent} />
//   </View>
// );

// /*
// |--------------------------------------------------------------------------
// | MAIN PDF COMPONENT
// |--------------------------------------------------------------------------
// */

// const StudentIDCardPDF = ({
//   students = [],
//   academicYear = null,
// }) => {
//   const pages = [];

//   for (let index = 0; index < students.length; index += 9) {
//     pages.push(students.slice(index, index + 9));
//   }

//   return (
//     <Document
//       title="Excellent Kids Foundation — Student ID Cards"
//       author="Excellent Kids Foundation"
//       subject="Official Student Identification Cards"
//     >
//       {pages.map((pageStudents, pageIndex) => {
//         const slots = createNineSlots(pageStudents);

//         return (
//           <React.Fragment key={`card-sheet-${pageIndex}`}>
//             {/* FRONT SHEET */}

//             <Page
//               size="A4"
//               orientation="landscape"
//               style={styles.page}
//             >
//               <View style={styles.grid}>
//                 {slots.map((student, index) => (
//                   <View
//                     key={`front-${pageIndex}-${index}`}
//                     style={styles.cardWrapper}
//                   >
//                     {student && (
//                       <StudentFrontCard
//                         student={student}
//                         academicYear={academicYear}
//                       />
//                     )}
//                   </View>
//                 ))}
//               </View>
//             </Page>

//             {/* BACK SHEET */}

//             <Page
//               size="A4"
//               orientation="landscape"
//               style={styles.page}
//             >
//               <View style={styles.grid}>
//                 {slots.map((student, index) => (
//                   <View
//                     key={`back-${pageIndex}-${index}`}
//                     style={styles.cardWrapper}
//                   >
//                     {student && <StudentBackCard />}
//                   </View>
//                 ))}
//               </View>
//             </Page>
//           </React.Fragment>
//         );
//       })}
//     </Document>
//   );
// };

// export default StudentIDCardPDF;